import {useCallback} from 'react';

import {FeatureFlag} from 'core/types';
import {
  AppPermissionId,
  EnnablRoleHierarchyType,
  EnnablRoleType,
  EnnablUserViewMode,
} from 'modules/decisionsNext/api/types';

import {FeatureFlags, FeatureFlagsGroup} from 'core/constants/featureFlags';
import {AuthState, useAuthStore} from 'core/stores/auth';
import {fetchLocalFeatureFlags} from 'modules/modifier';
import {dayjs} from 'utils/dateUtils';
import {impersonationToken} from 'core/impersonation';
import {useProjectName} from 'utils/hooks/useProjectName';

export const useUserInfo = () => useAuthStore(state => state.userInfo) as Exclude<AuthState['userInfo'], undefined>;

/**
 * @deprecated Non-reactive getter, use only in actions
 */
export const getUserInfo = () => {
  return useAuthStore.getState().userInfo;
};

export const useIsInternalUser = () => {
  const {profile} = useUserInfo();

  return profile?.email?.endsWith('@ennabl.com') || impersonationToken;
};

export const useAppPermissions = () => {
  const appPermissions = useAuthStore(state => state.userInfo?.permissions);

  return {
    hasPermission: useCallback(
      (permissions: AppPermissionId | AppPermissionId[]) => {
        const permissionsArr = Array.isArray(permissions) ? permissions : [permissions];
        return permissionsArr.every(permission => appPermissions?.includes(permission));
      },
      [appPermissions]
    ),
  };
};

// TODO: Remove when all tenants are migrated to new permissions
export const useRestrictedAccessPermissions = () => {
  const withNewPermissions = useFeatureFlag(FeatureFlags.UserManagement);
  const {hasPermission} = useAppPermissions();

  const onlyReportStudio = hasPermission(AppPermissionId.OnlyReportStudio);
  const onlyDocuments = hasPermission(AppPermissionId.OnlyDocuments);

  if (withNewPermissions) {
    return {
      restrictedAccess: false,
      onlyReportStudioPermission: false,
      onlyDocumentsPermission: false,
    };
  }

  return {
    restrictedAccess: onlyReportStudio || onlyDocuments,
    onlyReportStudioPermission: onlyReportStudio,
    onlyDocumentsPermission: onlyDocuments,
  };
};

const checkFeatureFlagInternal = (tenantId: string, flag?: FeatureFlag) => {
  if (!flag) {
    return false;
  }

  const {[flag.name]: modified} = fetchLocalFeatureFlags();

  if (modified !== undefined) {
    const utcDate = dayjs(modified.activeAfterUtcDate);

    return modified.activeAfterUtcDate ? modified.enabled && dayjs().utc().isAfter(utcDate) : modified.enabled;
  }

  const utcDate = dayjs(flag.activeAfterUtcDate);
  const enabled = flag.activeAfterUtcDate ? flag.enabled && dayjs().utc().isAfter(utcDate) : flag.enabled;

  if (enabled) {
    if (flag.activeForTenants) {
      return flag.activeForTenants.includes(tenantId);
    }

    if (flag.inactiveForTenants) {
      return !flag.inactiveForTenants.includes(tenantId);
    }

    return true;
  }

  return false;
};

export const useFeatureFlag = (name: FeatureFlag['name']): boolean => {
  const userInfo = useUserInfo();

  const flag = useAuthStore(state => state.flags?.find(flag => flag.name === name));

  return checkFeatureFlagInternal(userInfo?.tenantId, flag);
};

export const useFeatureFlagGroup = (name: FeatureFlagsGroup): boolean => {
  const userInfo = useUserInfo();

  const flags = useAuthStore(state => state.flags?.filter(flag => flag.group === name));

  return flags?.some(flag => checkFeatureFlagInternal(userInfo?.tenantId, flag)) ?? false;
};

/**
 * @deprecated Non-reactive getter, use only in actions
 */
export const checkFeatureFlagGroup = (name: FeatureFlagsGroup) => {
  const tenantId = useAuthStore.getState().userInfo?.tenantId as string;
  const flags = useAuthStore.getState().flags?.filter(flag => flag.group === name);

  return flags?.some(flag => checkFeatureFlagInternal(tenantId, flag)) ?? false;
};

/**
 * @deprecated Non-reactive getter, use only in actions
 */
export const checkFeatureFlag = (name: FeatureFlag['name'], tenantId?: string) => {
  const storedTenantId = useAuthStore.getState().userInfo?.tenantId as string;
  const effectiveTenantId = tenantId || storedTenantId;
  const flag = useAuthStore.getState().flags?.find(flag => flag.name === name);

  return checkFeatureFlagInternal(effectiveTenantId, flag);
};

export const useFeatureFlagCheck = () => {
  const userInfo = useUserInfo();
  const flags = useAuthStore(state => state.flags);

  return useCallback(
    (name: FeatureFlag['name']) => {
      const flag = flags?.find(f => f.name === name);
      return checkFeatureFlagInternal(userInfo?.tenantId, flag);
    },
    [flags, userInfo?.tenantId]
  );
};

// TODO: Remove when all tenants are migrated to new permissions
/** @deprecated Use useEntityAccess instead */
export const useIsExportHidden = () => {
  const {hasPermission} = useAppPermissions();

  return !hasPermission(AppPermissionId.ExportDecisions);
};

const useIsRoleType = (roleType: EnnablRoleType) => useAuthStore(state => state.userInfo?.roleType === roleType);

// TODO: Refactor when all tenants are migrated to new permissions
/** Admin with Admin/Custom roles */
export const useIsAdmin = () => {
  const withNewPermissions = useFeatureFlag(FeatureFlags.UserManagement);
  const isAdminRole = useIsRoleType(EnnablRoleType.Admin);
  const isCustomRole = useIsRoleType(EnnablRoleType.Custom);
  const isAdminRoleOld = isAdminRole || isCustomRole;
  const {role} = useUserInfo();

  return withNewPermissions ? role?.viewMode === EnnablUserViewMode.DecisionsDefault : isAdminRoleOld;
};

// TODO: Refactor when all tenants are migrated to new permissions
export const useIsProducer = () => {
  const withNewPermissions = useFeatureFlag(FeatureFlags.UserManagement);
  const isProducerRoleOld = useIsRoleType(EnnablRoleType.Producer);
  const {role} = useUserInfo();

  return withNewPermissions ? role?.viewMode === EnnablUserViewMode.DecisionsProducer : isProducerRoleOld;
};

// TODO: Remove when all tenants are migrated to new permissions
/** @deprecated Use useEntityAccess or useProjectName instead */
export const useIsMgaDashboard = () => {
  const tenantId = useAuthStore.getState().userInfo?.tenantId;

  return useFeatureFlag(FeatureFlags.MGADashboard) || tenantId === 'expmgad';
};

export const useWithClientTags = () => {
  const {isMga} = useProjectName();

  return useFeatureFlag(FeatureFlags.ClientTagsFilter) && !isMga;
};

export const useWithPolicyTags = () => {
  const {isMga} = useProjectName();

  return useFeatureFlag(FeatureFlags.PolicyTagsFilter) && !isMga;
};

/** Root admin check for user management (checks EnnablRoleHierarchyType.Root, not the legacy EnnablRoleType) */
export const useIsRootAdmin = () => {
  const {role} = useUserInfo();

  return role?.roleType === EnnablRoleHierarchyType.Root;
};
